/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PersonType } from 'types/enums/PersonType'
import { ArticleGroup } from 'types/entities/ArticleGroup'
import { Article } from 'types/entities/Article'
import { ArticlePersonsType } from 'types/entities/ArticlePersonsType'
import { ArticleType } from 'types/enums/ArticleType'
import { ArticleTicketType } from 'types/enums/ArticleTicketType'
import type {Package} from "types/entities/Package";

export interface ArticlesState {
    all: Article[]
    online: ArticlePersonsType
    action: ArticlePersonsType
    vouchers: Article[]
    yearly: Article[]
    threetwo: Article[]
    articlesKiosk: Article[]
    articleGroups: ArticleGroup[],
	packages: Package[],
}

const initialState: ArticlesState = {
    all: [],
    online: { adult: [], child: [] },
    action: { adult: [], child: [] },
    vouchers: [],
    yearly: [],
    threetwo: [],
    articlesKiosk: [],
    articleGroups: [],
	packages: [],

}

export type ArticlesReducerSetArticlesAction = PayloadAction<Article[]>
export type ArticlesReducerSetArticlesKioskAction = PayloadAction<Article[]>
export type ArticlesReducerSetArticleGroupsAction = PayloadAction<
    ArticleGroup[]
>

const articlesSlice = createSlice({
    name: 'articles',
    initialState,
    reducers: {
        setArticles(state, action: PayloadAction<Article[]>) {
            const articles = action.payload ?? []
            const onlineTickets = articles.filter(
                (a) => a.type === ArticleType.Online
            )
            const actionTickets = articles.filter(
                (a) => a.type === ArticleType.Action
            )
            const voucherTickets = articles.filter(
                (a) =>
                    a.type === ArticleType.Online &&
                    a.ticket_type === ArticleTicketType.Voucher
            )
            const yearlyTickets = articles.filter(
                (a) =>
                    a.type === ArticleType.Yearly &&
                    a.ticket_type === ArticleTicketType.Voucher
            )
            const threeTwoTickets = articles.filter(
                (a) =>
                    a.type === ArticleType.ThreeTwo &&
                    a.ticket_type === ArticleTicketType.Voucher
            )

            state.all = articles
            state.online.adult = onlineTickets.filter(
                (a) => a.person_type === PersonType.Adult
            )
            state.online.child = onlineTickets.filter(
                (a) => a.person_type === PersonType.Child
            )
            state.action.adult = actionTickets.filter(
                (a) => a.person_type === PersonType.Adult
            )
            state.action.child = actionTickets.filter(
                (a) => a.person_type === PersonType.Child
            )
            state.vouchers = voucherTickets
            state.yearly = yearlyTickets
            state.threetwo = threeTwoTickets
        },
        setArticlesKiosk(state, action: PayloadAction<Article[]>) {
            state.articlesKiosk = action.payload
        },
        setArticleGroups(state, action: PayloadAction<ArticleGroup[]>) {
            state.articleGroups = action.payload
        },
		setPackages(state, action: PayloadAction<Package[]>) {
			state.packages = action.payload
		},
		resetState: () => initialState,
    },
})

export const { setArticles, setArticlesKiosk, setArticleGroups, setPackages, resetState } =
    articlesSlice.actions
export default articlesSlice.reducer
